import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Users, Home, Euro, PieChart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function RatioGauge({ label, value, max, unit = '%', icon: Icon, color }) {
  const pct = Math.min((value / max) * 100, 120);
  const isOver = value > max;
  const barColor = isOver ? '#ef4444' : value > max * 0.9 ? '#f59e0b' : color;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all" data-testid={`ratio-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
            <Icon size={18} style={{ color }} />
          </div>
          <span className="text-xs font-semibold text-gray-700">{label}</span>
        </div>
        {isOver ? (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50">
            <AlertTriangle size={12} className="text-red-500" />
            <span className="text-[9px] text-red-600 font-bold">DEPASSE</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50">
            <CheckCircle size={12} className="text-emerald-500" />
            <span className="text-[9px] text-emerald-600 font-bold">OK</span>
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-3xl font-bold" style={{ color: barColor }}>{value}</span>
        <span className="text-sm text-gray-400">{unit}</span>
        <span className="text-xs text-gray-300 ml-1">/ {max}{unit} max</span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden relative">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
        />
        {/* Threshold marker */}
        <div className="absolute top-0 h-full w-0.5 bg-gray-400" style={{ left: '100%', transform: 'translateX(-1px)' }} />
      </div>
    </div>
  );
}

export default function RatiosCles() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/ca/ratios-cles`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-[#009893] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !data.mois?.length) {
    return <div className="text-center py-12 text-gray-400">Aucune donnee disponible</div>;
  }

  const current = data.mois[0];
  const prev = data.mois[1];
  const seuils = data.seuils;

  const formatK = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;

  const trend = (cur, prv) => {
    if (!prv || prv === 0) return null;
    const diff = ((cur - prv) / prv * 100).toFixed(1);
    return { diff: parseFloat(diff), up: parseFloat(diff) > 0 };
  };

  const caTrend = trend(current.ca_ht, prev?.ca_ht);
  const msTrend = trend(current.pct_masse_salariale, prev?.pct_masse_salariale);

  return (
    <div className="space-y-6" data-testid="ratios-cles-page">
      {/* Header */}
      <div>
        <h2 className="text-base font-bold text-gray-900">Ratios Cles</h2>
        <p className="text-[10px] text-gray-400">Surveillance des postes majeurs — {current.mois}</p>
      </div>

      {/* CA summary */}
      <div className="bg-gradient-to-r from-[#1B2A52] to-[#2a3d6e] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-white/50 uppercase tracking-wider">Chiffre d'affaires HT</p>
            <p className="text-3xl font-bold mt-1">{current.ca_ht.toLocaleString('fr-FR')}€</p>
            {caTrend && (
              <div className="flex items-center gap-1 mt-2">
                {caTrend.up ? <TrendingUp size={14} className="text-emerald-400" /> : <TrendingDown size={14} className="text-red-400" />}
                <span className={`text-xs ${caTrend.up ? 'text-emerald-400' : 'text-red-400'}`}>{caTrend.up ? '+' : ''}{caTrend.diff}% vs mois precedent</span>
              </div>
            )}
          </div>
          <div className="text-right space-y-1">
            <p className="text-[10px] text-white/40">{current.nb_employes} employe{current.nb_employes > 1 ? 's' : ''}</p>
            <p className="text-[10px] text-white/40">Loyer: {data.loyer_mensuel}€/mois</p>
          </div>
        </div>
      </div>

      {/* Ratio gauges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RatioGauge
          label="Food cost"
          value={current.pct_food_cost}
          max={seuils.food_cost_max}
          icon={Euro}
          color="#E07A5F"
        />
        <RatioGauge
          label="Masse salariale"
          value={current.pct_masse_salariale}
          max={seuils.masse_salariale_max}
          icon={Users}
          color="#2563EB"
        />
        <RatioGauge
          label="Loyer"
          value={current.pct_loyer}
          max={seuils.loyer_max}
          icon={Home}
          color="#7C3AED"
        />
        <RatioGauge
          label="Total 3 postes"
          value={current.pct_total_3_postes}
          max={seuils.total_3_postes_max}
          icon={PieChart}
          color="#009893"
        />
      </div>

      {/* Detail breakdown */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Detail — {current.mois}</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-600">CA HT</span>
            <span className="text-sm font-bold text-gray-900">{current.ca_ht.toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <div>
              <span className="text-sm text-gray-600">Achats matieres premieres</span>
              <p className="text-[10px] text-gray-400">Hors essence & frais divers</p>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold" style={{ color: current.alerte_food_cost ? '#ef4444' : '#E07A5F' }}>{current.achats_matieres_ht?.toLocaleString('fr-FR') || 0}€</span>
              <p className="text-[10px] text-gray-400">{current.pct_food_cost}% du CA</p>
            </div>
          </div>
          {current.frais_divers_ht > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-600">Frais divers (vehicule, etc.)</span>
              <span className="text-sm text-gray-500">{current.frais_divers_ht?.toLocaleString('fr-FR') || 0}€</span>
            </div>
          )}
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <div>
              <span className="text-sm text-gray-600">Masse salariale brute</span>
              <p className="text-[10px] text-gray-400">
                {current.nb_employes} employe{current.nb_employes > 1 ? 's' : ''}
                {current.ms_source_mois && current.ms_source_mois !== current.mois && (
                  <span className="text-amber-500"> (base {current.ms_source_mois})</span>
                )}
              </p>
            </div>
            <span className="text-sm font-bold text-gray-900">{current.masse_salariale_brut.toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-600">Charges patronales</span>
            <span className="text-sm font-bold text-gray-900">{current.charges_patronales.toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-600">Total masse salariale</span>
            <div className="text-right">
              <span className="text-sm font-bold" style={{ color: current.alerte_masse_salariale ? '#ef4444' : '#2563EB' }}>{current.masse_salariale.toLocaleString('fr-FR')}€</span>
              <p className="text-[10px] text-gray-400">{current.pct_masse_salariale}% du CA</p>
            </div>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-600">Loyer</span>
            <div className="text-right">
              <span className="text-sm font-bold" style={{ color: current.alerte_loyer ? '#ef4444' : '#7C3AED' }}>{current.loyer.toLocaleString('fr-FR')}€</span>
              <p className="text-[10px] text-gray-400">{current.pct_loyer}% du CA</p>
            </div>
          </div>
          <div className="flex items-center justify-between py-3 bg-gray-50 rounded-xl px-3 -mx-1">
            <span className="text-sm font-bold text-gray-800">Total 3 postes</span>
            <div className="text-right">
              <span className="text-base font-bold" style={{ color: current.alerte_total ? '#ef4444' : '#009893' }}>
                {(current.masse_salariale + current.loyer).toLocaleString('fr-FR')}€
              </span>
              <p className="text-[10px]" style={{ color: current.alerte_total ? '#ef4444' : '#009893' }}>
                {current.pct_total_3_postes}% du CA (max 75%)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Historical evolution */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Evolution sur 12 mois</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-gray-400 font-medium">Mois</th>
                <th className="text-right py-2 text-gray-400 font-medium">CA HT</th>
                <th className="text-right py-2 text-gray-400 font-medium">% Food</th>
                <th className="text-right py-2 text-gray-400 font-medium">% MS</th>
                <th className="text-right py-2 text-gray-400 font-medium">% Loyer</th>
                <th className="text-right py-2 text-gray-400 font-medium">% Total</th>
              </tr>
            </thead>
            <tbody>
              {data.mois.filter(m => m.ca_ht > 0).map((m, i) => (
                <tr key={m.mois} className={`border-b border-gray-50 ${i === 0 ? 'bg-blue-50/30' : ''}`}>
                  <td className="py-2 font-medium text-gray-700">{m.mois}</td>
                  <td className="py-2 text-right text-gray-600">{formatK(m.ca_ht)}€</td>
                  <td className="py-2 text-right font-bold" style={{ color: m.alerte_food_cost ? '#ef4444' : '#E07A5F' }}>{m.pct_food_cost}%</td>
                  <td className="py-2 text-right font-bold" style={{ color: m.alerte_masse_salariale ? '#ef4444' : '#2563EB' }}>{m.pct_masse_salariale}%</td>
                  <td className="py-2 text-right font-bold" style={{ color: m.alerte_loyer ? '#ef4444' : '#7C3AED' }}>{m.pct_loyer}%</td>
                  <td className="py-2 text-right font-bold" style={{ color: m.alerte_total ? '#ef4444' : '#009893' }}>{m.pct_total_3_postes}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seuils reminder */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <p className="text-[10px] text-gray-500 font-medium mb-2">SEUILS DE REFERENCE</p>
        <div className="flex gap-4 flex-wrap text-[10px] text-gray-500">
          <span>Food cost : max <strong>{seuils.food_cost_max}%</strong> du CA</span>
          <span>Masse salariale : max <strong>{seuils.masse_salariale_max}%</strong> du CA</span>
          <span>Loyer : max <strong>{seuils.loyer_max}%</strong> du CA</span>
          <span>Total 3 postes : max <strong>{seuils.total_3_postes_max}%</strong> du CA</span>
        </div>
      </div>
    </div>
  );
}
